import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Shield, KeyRound, CheckCircle, Bot, Zap, Circle, Network } from 'lucide-react';
import { githubAuth, GitHubUser } from '../services/githubAuth';
import { OWASP_LLM_TOP_10, OWASP_LLM_CHECKED_COUNT } from '../data/owaspLlmChecklist';
import HeuristicNotice from './HeuristicNotice';

const GithubIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.34-3.369-1.34-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
  </svg>
);

// Temporarily hidden while the VS Code extension has issues. Set to true to restore.
const SHOW_VSCODE_BANNER = false;

const Home: React.FC = () => {
  const [repoUrl, setRepoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<GitHubUser | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setUser(githubAuth.getUser());
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!repoUrl.trim()) return;
    setIsLoading(true);
    navigate(`/analyze?repo=${encodeURIComponent(repoUrl.trim())}`, {
      state: {
        repoUrl: repoUrl.trim(),
        githubToken: githubAuth.getToken() || undefined,
      },
    });
  };

  const features = [
    {
      icon: <Bot className="h-6 w-6" />,
      title: "OWASP LLM Top 10",
      description: `Heuristic signals for ${OWASP_LLM_CHECKED_COUNT} of 10 OWASP LLM risks — prompt injection, excessive agency, improper output handling, and more`
    },
    {
      icon: <KeyRound className="h-6 w-6" />,
      title: "Secret & Key Scanning",
      description: "30+ named token patterns (AWS, OpenAI, Anthropic, Stripe…) plus Shannon entropy detection for unknown secrets"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "OWASP Top 10 + CVEs",
      description: "SQL injection, XSS, SSRF, command injection — plus live CVE lookup via OSV.dev for all your dependencies"
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Vibe-Code Risk Engine",
      description: "33 AI-specific patterns: unsafe model loading, training data exposure, LLM jacking creds, Gradio/Ray framework CVEs, LangChain agent risks, and more"
    }
  ];

  return (
    <>
    <Helmet>
      <title>GitHubMate — Free AI Code Security Scanner | Security Knowledge Graph | OWASP LLM Top 10</title>
      <meta name="description" content="Map your code's attack surface with GitHubMate's Security Knowledge Graph — vulnerabilities, secrets, and CVEs connected to files, OWASP categories, and compliance frameworks in one interactive graph. Full OWASP LLM Top 10 (2025), AI Fix Generator, IaC scanning — free, no signup, 30 seconds." />
      <link rel="canonical" href="https://githubmate.ai/" />
    </Helmet>
    <div className="min-h-full bg-charcoal-bg relative overflow-hidden">
      {/* Radial hero glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[520px] rounded-full bg-[radial-gradient(ellipse,rgba(159,232,112,0.13)_0%,rgba(180,155,245,0.08)_45%,transparent_70%)]"
      />
      <div className="container mx-auto px-4 py-16 relative">
        {/* Hero: copy left, scan card right */}
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto mb-16">
          <div className="text-center lg:text-left">
            <span className="eyebrow mb-6">● free · no signup · 30-second scan</span>

            <h1 className="font-display text-5xl md:text-6xl font-extrabold text-gray-900 leading-[1.05] tracking-tight mb-5">
              Security Scanner for
              <span className="text-lime block">AI-Generated Code</span>
            </h1>

            <p className="text-lg text-muted max-w-xl mx-auto lg:mx-0">
              Scan any GitHub repository for secrets, vulnerabilities, and dependency risks —
              with enhanced detection for patterns common in vibe-coded and AI-assisted projects.
              <span className="font-semibold text-gray-900"> Powered by AI-driven analysis.</span>
            </p>
          </div>

          {/* Scan Form */}
          <form onSubmit={handleSubmit} className="bg-charcoal-card border border-charcoal-border rounded-2xl shadow-2xl p-7">

            {/* GitHub Auth Status */}
            {user ? (
              <div className="flex items-center justify-between mb-6 p-3 bg-limedim border border-lime/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <img
                    src={user.avatar_url}
                    alt={user.login}
                    className="w-8 h-8 rounded-full ring-2 ring-lime"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {user.name || user.login}
                    </p>
                    <p className="text-xs text-lime flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Connected · 5,000 requests/hr
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { githubAuth.logout(); setUser(null); }}
                  className="text-xs text-muted hover:text-sev-critical transition-colors"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={githubAuth.login}
                  className="w-full flex items-center justify-center gap-3 bg-white text-charcoal-ink py-3 px-6 rounded-full font-semibold hover:bg-gray-800 transition-colors"
                >
                  <GithubIcon className="h-5 w-5" />
                  Login with GitHub
                </button>
                <p className="text-xs text-faint text-center mt-2">
                  Unlocks 5,000 requests/hr and private repo scanning.
                  Without login, limited to 60 requests/hr (public repos only).
                </p>
              </div>
            )}

            {/* Repo URL Input */}
            <div className="mb-5">
              <label htmlFor="repoUrl" className="block text-sm font-medium text-muted mb-2">
                GitHub Repository URL
              </label>
              <div className="relative">
                <GithubIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-faint" />
                <input
                  type="url"
                  id="repoUrl"
                  value={repoUrl}
                  onChange={(e) => setRepoUrl(e.target.value)}
                  placeholder="https://github.com/username/repository"
                  className="input-field !pl-10"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !repoUrl.trim()}
              className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-charcoal-ink"></div>
                  <span>Scanning...</span>
                </>
              ) : (
                <>
                  <Search className="h-5 w-5" />
                  <span>Scan Repository</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* VSCode Extension Banner — hidden while the extension has issues.
            Flip SHOW_VSCODE_BANNER to true to restore. */}
        {SHOW_VSCODE_BANNER && (
        <div className="max-w-2xl mx-auto mb-16">
          <a
            href="https://marketplace.visualstudio.com/items?itemName=ManojAlwis.githubmate-vscode"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 bg-charcoal-card border border-charcoal-border rounded-2xl p-5 shadow-md hover:shadow-xl hover:border-lime/50 transition-all duration-200"
          >
            {/* VS Code Icon */}
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#007ACC] flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-200">
              <svg viewBox="0 0 100 100" className="w-7 h-7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M74.5 6.01L52 31.51L30 12.51L6.5 22.01L6.5 78.01L30 87.51L52 68.51L74.5 94.01L93.5 84.51V15.51L74.5 6.01ZM74.5 74.01L52 50.01L74.5 26.01V74.01ZM30 62.01L18.5 50.01L30 38.01L41.5 50.01L30 62.01Z" fill="white"/>
              </svg>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-bold text-gray-900">GitHubMate for VS Code</span>
                <span className="inline-flex items-center gap-1 bg-limedim border border-lime/30 text-lime text-xs font-semibold px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-lime inline-block"></span>
                  New
                </span>
              </div>
              <p className="text-xs text-muted truncate">
                Scan any GitHub repo right inside your editor — inline diagnostics, AI analysis, SBOM export
              </p>
            </div>

            {/* Install Button */}
            <div className="flex-shrink-0 flex items-center gap-2">
              <span className="hidden sm:inline-flex items-center gap-1.5 bg-lime text-charcoal-ink text-xs font-semibold px-3 py-1.5 rounded-full transition-colors">
                Install Extension
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </span>
              <svg className="sm:hidden w-4 h-4 text-lime" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
          </a>
        </div>
        )}

        {/* Stats strip — citable facts for GEO */}
        <div className="max-w-4xl mx-auto mb-16">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '30+', label: 'Secret patterns detected' },
              { value: '8', label: 'Dependency ecosystems' },
              { value: '20', label: 'OWASP categories covered' },
              { value: '30s', label: 'Scan time, no signup' },
            ].map(stat => (
              <div key={stat.label} className="card !p-5 text-center">
                <dt className="font-display text-4xl font-extrabold text-lime">{stat.value}</dt>
                <dd className="text-sm text-muted mt-1">{stat.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Security Knowledge Graph Spotlight */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-lime/8 via-charcoal-card to-accent-teal/5 border border-lime/25 rounded-3xl p-8 md:p-10 overflow-hidden relative">
            {/* Glow accent */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[radial-gradient(ellipse,rgba(159,232,112,0.12)_0%,transparent_70%)]"
            />
            <div className="grid lg:grid-cols-2 gap-10 items-stretch relative">

              {/* Copy */}
              <div>
                <div className="flex items-center gap-2 mb-5">
                  <span className="inline-flex items-center gap-1.5 bg-limedim border border-lime/30 text-lime text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                    <Network className="h-3.5 w-3.5" />
                    New · Exclusive to GitHubMate
                  </span>
                </div>

                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
                  Map Your Code's{' '}
                  <span className="text-lime">Attack Surface</span>
                </h2>

                <p className="text-muted text-base mb-6 max-w-lg leading-relaxed">
                  GitHubMate's <strong className="text-gray-900">Security Knowledge Graph</strong> maps every vulnerability, secret, and CVE to the files, OWASP categories, and compliance frameworks they affect — in one interactive graph. No competitor does this for free, in the browser.
                </p>

                <ul className="space-y-3 mb-7 text-sm text-muted">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle className="h-4 w-4 text-lime flex-shrink-0 mt-0.5" />
                    <span><strong className="text-gray-900">Top 3 Fix-First callout</strong> — blast-radius scoring tells you exactly where to start</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle className="h-4 w-4 text-lime flex-shrink-0 mt-0.5" />
                    <span><strong className="text-gray-900">AI Fix Generator</strong> — click any vuln node, get ready-to-paste code in seconds</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle className="h-4 w-4 text-lime flex-shrink-0 mt-0.5" />
                    <span><strong className="text-gray-900">Compliance links</strong> — see which vulns are blocking SOC 2, GDPR, HIPAA, PCI DSS</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle className="h-4 w-4 text-lime flex-shrink-0 mt-0.5" />
                    <span><strong className="text-gray-900">Filter by severity & type</strong> — zoom in on Critical nodes, or isolate secrets and deps</span>
                  </li>
                </ul>

              </div>

              {/* Mini-graph preview */}
              <div className="relative rounded-2xl overflow-hidden bg-charcoal-card border border-charcoal-border min-h-[320px]">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 420 260"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-label="Preview of GitHubMate Security Knowledge Graph showing vulnerabilities connected to files, OWASP categories, and compliance frameworks"
                >
                  {/* Edges */}
                  <line x1="82" y1="113" x2="174" y2="108" stroke="#4b5563" strokeWidth="1.5" strokeDasharray="4 3" />
                  <line x1="82" y1="155" x2="174" y2="135" stroke="#4b5563" strokeWidth="1.5" strokeDasharray="4 3" />
                  <line x1="68" y1="46" x2="174" y2="100" stroke="#d97706" strokeWidth="1.5" strokeDasharray="4 3" />
                  <line x1="256" y1="108" x2="315" y2="70" stroke="#9fe870" strokeWidth="2" />
                  <line x1="256" y1="135" x2="315" y2="195" stroke="#9fe870" strokeWidth="1.5" strokeDasharray="4 3" />
                  <line x1="215" y1="165" x2="215" y2="210" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="4 3" />

                  {/* Secret node — top */}
                  <rect x="18" y="30" width="100" height="30" rx="7" fill="#1c1400" stroke="#d97706" strokeWidth="1.5" />
                  <text x="68" y="50" textAnchor="middle" fill="#d97706" fontSize="9" fontWeight="700">API_KEY leaked</text>

                  {/* File nodes — left */}
                  <rect x="18" y="98" width="64" height="28" rx="6" fill="#1a1f2e" stroke="#374151" strokeWidth="1" />
                  <text x="50" y="116" textAnchor="middle" fill="#9ca3af" fontSize="8.5">auth.js</text>

                  <rect x="18" y="140" width="64" height="28" rx="6" fill="#1a1f2e" stroke="#374151" strokeWidth="1" />
                  <text x="50" y="158" textAnchor="middle" fill="#9ca3af" fontSize="8.5">api.ts</text>

                  {/* Critical vuln node — centre */}
                  <rect x="170" y="88" width="90" height="58" rx="9" fill="#1e0505" stroke="#ef4444" strokeWidth="2.5" />
                  <rect x="170" y="88" width="90" height="20" rx="9" fill="#ef4444" />
                  <rect x="170" y="100" width="90" height="8" fill="#ef4444" />
                  <text x="215" y="102" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="800">CRITICAL</text>
                  <text x="215" y="118" textAnchor="middle" fill="#fca5a5" fontSize="9.5" fontWeight="700">SQL Injection</text>
                  <text x="215" y="133" textAnchor="middle" fill="#6b7280" fontSize="8">2 files · OWASP A03</text>

                  {/* OWASP node — top-right (lime/teal themed) */}
                  <rect x="310" y="50" width="96" height="42" rx="7" fill="#0e1a0e" stroke="#9fe870" strokeWidth="1.5" />
                  <text x="358" y="67" textAnchor="middle" fill="#9fe870" fontSize="9" fontWeight="700">OWASP A03</text>
                  <text x="358" y="82" textAnchor="middle" fill="#9ca3af" fontSize="8.5">Injection</text>

                  {/* Compliance node — bottom-right (teal themed) */}
                  <rect x="310" y="177" width="96" height="42" rx="7" fill="#071a1a" stroke="#2dd4bf" strokeWidth="1.5" />
                  <text x="358" y="194" textAnchor="middle" fill="#2dd4bf" fontSize="9" fontWeight="700">SOC 2 ✗</text>
                  <text x="358" y="209" textAnchor="middle" fill="#9ca3af" fontSize="8.5">CC6.1 blocked</text>

                  {/* Dep CVE node — bottom-center */}
                  <rect x="168" y="210" width="94" height="38" rx="7" fill="#0d1428" stroke="#3b82f6" strokeWidth="1.5" />
                  <text x="215" y="226" textAnchor="middle" fill="#93c5fd" fontSize="8.5" fontWeight="700">CVE-2024-1234</text>
                  <text x="215" y="240" textAnchor="middle" fill="#6b7280" fontSize="8">lodash · HIGH</text>

                  {/* Legend dots */}
                  <circle cx="20" cy="255" r="4" fill="#ef4444" />
                  <text x="29" y="259" fill="#6b7280" fontSize="8">Vuln</text>
                  <circle cx="68" cy="255" r="4" fill="#4b5563" />
                  <text x="77" y="259" fill="#6b7280" fontSize="8">File</text>
                  <circle cx="108" cy="255" r="4" fill="#9fe870" />
                  <text x="117" y="259" fill="#6b7280" fontSize="8">OWASP</text>
                  <circle cx="165" cy="255" r="4" fill="#2dd4bf" />
                  <text x="174" y="259" fill="#6b7280" fontSize="8">Compliance</text>
                  <circle cx="231" cy="255" r="4" fill="#d97706" />
                  <text x="240" y="259" fill="#6b7280" fontSize="8">Secret</text>
                  <circle cx="278" cy="255" r="4" fill="#3b82f6" />
                  <text x="287" y="259" fill="#6b7280" fontSize="8">CVE</text>
                </svg>

                {/* Overlay label */}
                <div className="absolute bottom-2 right-3 text-[10px] text-faint italic">
                  Live & interactive after scan
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {features.map((feature, index) => (
            <div key={index} className="card">
              <div className="w-11 h-11 rounded-xl bg-limedim border border-lime/25 flex items-center justify-center text-lime mb-4">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-muted text-sm">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* How it works — crawlable content for SEO */}
        <div className="mb-16 max-w-4xl mx-auto">
          <h2 className="font-display text-3xl font-extrabold text-gray-900 text-center mb-8">How GitHubMate Works</h2>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="card">
              <div className="font-display text-3xl font-extrabold text-lime mb-2">1</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Paste a GitHub URL</h3>
              <p className="text-sm text-muted">Enter any public GitHub repository URL. Optionally connect your GitHub account to unlock private repo scanning and 5,000 requests/hr.</p>
            </div>
            <div className="card">
              <div className="font-display text-3xl font-extrabold text-lime mb-2">2</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Instant Static Analysis</h3>
              <p className="text-sm text-muted">GitHubMate fetches your source files and runs SAST, secret scanning (Shannon entropy + 30+ named patterns), dependency CVE lookup via OSV.dev, and IaC checks — all in your browser, no backend.</p>
            </div>
            <div className="card">
              <div className="font-display text-3xl font-extrabold text-lime mb-2">3</div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">Security Report in 30 Seconds</h3>
              <p className="text-sm text-muted">Get a full security dashboard: OWASP Top 10 score, LLM Top 10 score, vibe-code risk score, CVE list with fix versions, compliance gaps (SOC 2, GDPR, HIPAA, PCI DSS), and an AI executive summary.</p>
            </div>
          </div>
        </div>

        {/* OWASP LLM Top 10 (2025) Signals */}
        <div className="mb-16">
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-widest text-accent-amber bg-accent-amber/10 border border-accent-amber/30 px-4 py-1.5 rounded-full">
              <Bot className="h-4 w-4" />
              OWASP LLM Top 10 (2025) — heuristic signals, {OWASP_LLM_CHECKED_COUNT} of 10 categories — free, no signup
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {OWASP_LLM_TOP_10.map(item => (
              <div
                key={item.id}
                className={`bg-charcoal-card border rounded-xl p-3 flex items-center gap-2 transition-colors ${item.checked ? 'border-charcoal-border hover:border-lime/45' : 'border-charcoal-border opacity-50'}`}
                aria-label={`${item.id} ${item.label}: ${item.checked ? 'heuristic signal available' : 'not yet checked'}`}
              >
                {item.checked
                  ? <CheckCircle aria-hidden="true" className="h-4 w-4 text-lime flex-shrink-0" />
                  : <Circle aria-hidden="true" className="h-4 w-4 text-faint flex-shrink-0" />}
                <div>
                  <div className="font-mono text-xs font-bold text-faint">{item.id}</div>
                  <div className="text-xs text-gray-900 font-medium leading-tight">{item.label}</div>
                  {!item.checked && (
                    <div className="text-[10px] text-faint uppercase tracking-wide mt-0.5">Not yet checked</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <HeuristicNotice context="llm" />
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="rounded-3xl border border-charcoal-border bg-gradient-to-br from-lime/15 via-charcoal-card to-accent-teal/10 p-10">
            <h2 className="font-display text-3xl font-extrabold text-gray-900 mb-4">
              Map Your Code's Attack Surface in 30 Seconds
            </h2>
            <p className="text-muted mb-2 max-w-xl mx-auto">
              Paste any GitHub URL and get an interactive Security Knowledge Graph — plus OWASP LLM Top 10, secrets, CVEs, compliance gaps, and an AI-generated fix for every vulnerability.
            </p>
            <p className="text-xs text-faint mb-6">Free · No signup · Runs entirely in your browser</p>
            <button
              onClick={() => document.getElementById('repoUrl')?.focus()}
              className="btn-primary px-8 py-3"
            >
              <Network className="inline h-4 w-4 mr-2 -mt-0.5" />
              Map My Attack Surface
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Home;
